import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, List, ShoppingCart, MapPin, CreditCard, Clock, CheckCircle, Phone, MessageCircle, HelpCircle } from 'lucide-react';
import { ListSection } from '../components/ios';

// iOS GuideSection: colored icon tile + title, numbered steps inside the group card.
function GuideSection({ number, title, icon: Icon, color, subtitle, steps }) {
  return (
    <ListSection title={`${number}. ${title}`}>
      <div className="px-4 py-3">
        <div className="flex items-center mb-3">
          <span
            className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center shrink-0 mr-3"
            style={{ backgroundColor: color }}
          >
            <Icon size={17} className="text-white" />
          </span>
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold text-gray-900">{subtitle}</span>
          </span>
        </div>
        <ol className="space-y-2">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start">
              <span
                className="text-white rounded-full w-5 h-5 flex items-center justify-center text-[11px] mr-2.5 shrink-0 mt-0.5"
                style={{ backgroundColor: color }}
              >
                {i + 1}
              </span>
              <span className="text-[15px] text-gray-700">{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </ListSection>
  );
}

function UserGuidePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar: back chevron + centered title */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-4 h-12 max-w-lg mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center -ml-2 text-[#FF3B30] active:opacity-60"
            aria-label="Back"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[16px] font-semibold text-gray-900">
            User Guide
          </h1>
          <span className="w-6" />
        </div>
      </header>

      <div className="max-w-lg mx-auto pb-8">
        {/* Welcome Section — iOS systemGray6 rounded card */}
        <div className="px-4 mt-4">
          <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
            <h2 className="text-[22px] font-bold text-gray-900 mb-1">Welcome to CheF!</h2>
            <p className="text-[15px] text-[#6D6D72]">
              This guide will help you order delicious food from CheF and track your deliveries.
            </p>
          </div>
        </div>

        <GuideSection
          number={1}
          title="Browse Menu"
          icon={List}
          color="#007AFF"
          subtitle="How to Browse — Find your favorite items easily"
          steps={[
            'Open the app and browse available restaurants',
            'Tap on CheF to view the full menu',
            'Browse categories like Chicken, Burgers, Sides, and Drinks',
            'Tap any item to see details and customization options',
          ]}
        />

        <GuideSection
          number={2}
          title="Add Items to Cart"
          icon={ShoppingCart}
          color="#34C759"
          subtitle="Building Your Order — Add items to your cart"
          steps={[
            'Select your desired items from the menu',
            'Choose quantity and any special instructions',
            "Tap 'Add to Cart' to save your selection",
            'View your cart by tapping the cart icon at the top',
          ]}
        />

        <GuideSection
          number={3}
          title="Choose Delivery or Pickup"
          icon={MapPin}
          color="#FF9500"
          subtitle="Delivery Options — Select how you want to receive your order"
          steps={[
            "Choose between 'Pickup' or 'Delivery'",
            'For delivery: Select or add your delivery address',
            'For pickup: No address needed',
            'Delivery fees are calculated based on distance',
          ]}
        />

        <GuideSection
          number={4}
          title="Checkout and Payment"
          icon={CreditCard}
          color="#AF52DE"
          subtitle="Secure Payment — Complete your order safely"
          steps={[
            'Review your order in the cart',
            "Tap 'Proceed to Checkout'",
            'Select or add a payment method',
            'Confirm and place your order',
          ]}
        />

        <GuideSection
          number={5}
          title="Track Your Order"
          icon={Clock}
          color="#5AC8FA"
          subtitle="Real-time Tracking — Follow your order every step of the way"
          steps={[
            "Go to 'Orders' from the main menu",
            'Tap on your active order to view details',
            'Watch real-time status updates',
            'For delivery: Track driver location on map',
          ]}
        />

        {/* Order Status Guide */}
        <ListSection title="6. Order Status Guide">
          <div className="px-4 py-3">
            <h4 className="text-[15px] font-semibold text-gray-900 mb-3">Understanding Order Statuses</h4>
            <div className="space-y-2">
              {[
                { label: 'Pending', desc: 'Order received, awaiting restaurant confirmation', color: 'text-[#FFCC00]' },
                { label: 'Confirmed', desc: 'Restaurant is preparing your order', color: 'text-[#007AFF]' },
                { label: 'Ready', desc: 'Order is ready for pickup/driver pickup', color: 'text-[#AF52DE]' },
                { label: 'On My Way', desc: 'Driver is en route with your order', color: 'text-[#FF9500]' },
                { label: 'Delivered', desc: 'Order has been successfully delivered', color: 'text-[#34C759]' },
              ].map((s) => (
                <div key={s.label} className="flex items-center p-3 bg-[#F2F2F7] rounded-lg">
                  <CheckCircle className={`${s.color} mr-3 shrink-0`} size={20} />
                  <div>
                    <p className="text-[15px] font-medium text-gray-900">{s.label}</p>
                    <p className="text-[13px] text-[#6D6D72]">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ListSection>

        {/* Support Section */}
        <ListSection title="Need Help?">
          <div className="px-4 py-3">
            <div className="space-y-2">
              <div className="flex items-start p-3 bg-[#F2F2F7] rounded-lg">
                <span className="w-[30px] h-[30px] rounded-[7px] bg-[#34C759] flex items-center justify-center shrink-0 mr-3">
                  <Phone size={17} className="text-white" />
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-gray-900">Call Support</p>
                  <p className="text-[15px] text-gray-700">1 869 660 1052</p>
                  <p className="text-[13px] text-[#6D6D72]">Available 9 AM - 9 PM</p>
                </div>
              </div>
              <div className="flex items-start p-3 bg-[#F2F2F7] rounded-lg">
                <span className="w-[30px] h-[30px] rounded-[7px] bg-[#007AFF] flex items-center justify-center shrink-0 mr-3">
                  <MessageCircle size={17} className="text-white" />
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-gray-900">Email Support</p>
                  <p className="text-[15px] text-gray-700">browneinc7@gmail.com</p>
                  <p className="text-[13px] text-[#6D6D72]">Response within 24 hours</p>
                </div>
              </div>
            </div>
          </div>
        </ListSection>
      </div>
    </div>
  );
}

export default UserGuidePage;
